import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { useGridApiRef } from '@mui/x-data-grid-pro'
import { Stack } from '@mui/material'
import DataGridWithSearch from 'components/generic/datagrid/DataGridWithSearch'
import { makeMaterialTypeToolTip } from 'utils/helpers'
import DialogModal from '..'

const OutboundTicketsModal = ({
  onAdd,
  onClose,
  outboundTicketsList,
  selectedOutboundTicketIds,
  pricePrepend,
  dataGridProps,
  ...restProps
}) => {
  const apiRef = useGridApiRef()
  const [selectedOutboundTickets, setSelectedOutboundTickets] = useState([])

  const { defaultYardUm } = useSelector(state => state.settings)

  const obColumns = useMemo(
    () => [
      {
        field: 'ticketId',
        headerName: '#',
        width: 60,
        editable: false,
        renderCell: params => {
          const { id, ticketId } = params.row
          return (
            <Link to={`/app/sales/outbound-tickets/${id}`} target="_blank">
              {ticketId}
            </Link>
          )
        },
      },
      {
        field: 'date',
        headerName: 'Effective Date',
        flex: 1.5,
        editable: false,
      },
      {
        field: 'salesOrderId',
        headerName: 'SO#',
        width: 60,
        editable: false,
      },
      {
        field: 'materials',
        headerName: 'Materials',
        flex: 1,
        sortable: false,
        renderCell: params => makeMaterialTypeToolTip(params.row.materials),
      },
      {
        field: 'netWeight',
        headerName: 'Net Weight',
        headerAlign: 'right',
        align: 'right',
        flex: 1,
        valueGetter: params => {
          const { netWeight } = params.row
          return `${netWeight} ${defaultYardUm}`
        },
        editable: false,
      },
      {
        field: 'value',
        headerName: 'Value',
        headerAlign: 'right',
        align: 'right',
        flex: 1,
        valueGetter: params => {
          const { value } = params.row
          return value || value === 0 ? `${pricePrepend}${value.toFixed(2)}` : ''
        },
        editable: false,
      },
      {
        field: 'status',
        headerName: 'Status',
        flex: 1,
        editable: false,
      },
      {
        field: 'user',
        headerName: 'User',
        flex: 1,
        editable: false,
      },
    ],
    [outboundTicketsList, pricePrepend, defaultYardUm],
  )

  return (
    <DialogModal
      {...restProps}
      onClose={() => {
        onClose()
      }}
      onSave={() => onAdd(selectedOutboundTickets)}
    >
      <Stack sx={{ height: 600 }}>
        <DataGridWithSearch
          density="compact"
          height="auto"
          rows={outboundTicketsList}
          columns={obColumns}
          apiRef={apiRef}
          editMode="row"
          checkboxSelection
          disableColumnMenu
          onSelectionModelChange={ids => {
            setSelectedOutboundTickets(ids)
          }}
          selectionModel={selectedOutboundTickets}
          onMounted={() => {
            setTimeout(() => {
              setSelectedOutboundTickets(selectedOutboundTicketIds)
            }, 100)
          }}
          {...dataGridProps}
        />
      </Stack>
    </DialogModal>
  )
}

OutboundTicketsModal.propTypes = {
  ...DialogModal.propTypes,
  outboundTicketsList: PropTypes.arrayOf(PropTypes.object),
  selectedOutboundTicketIds: PropTypes.array,
  dataGridProps: PropTypes.object,
  pricePrepend: PropTypes.string,
}

OutboundTicketsModal.defaultProps = {
  ...DialogModal.defaultProps,
  title: 'Import Tickets to Payment',
  outboundTicketsList: [],
  selectedOutboundTicketIds: [],
  dataGridProps: {},
  pricePrepend: '$',
}

export default OutboundTicketsModal
